/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { SkipToMainContent } from '@/components/blocks/SkipToMainContent';
import { CDNCacheOnloadMutation } from '@/components/content/CDNCacheOnLoadMutaion';
import { CustomerService } from '@/components/content/CustomerService';
import { Notifications } from '@/components/content/Notifications';
import { SelectedStoreSynchronizer } from '@/components/content/SelectedStoreSynchronizer';
import { SessionErrorDialog } from '@/components/content/SessionErrorDialog';
import { layoutManifest } from '@/components/layouts/manifest';
import { Meta } from '@/data/Meta';
import { useStaticSettings } from '@/data/Settings';
import { SettingProvider } from '@/data/context/setting';
import { Layout } from '@/data/types/Layout';
import { ThemeSettingsProvider } from '@/styles/theme';
import { CssBaseline, SxProps } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import Head from 'next/head';
import { FC, useEffect, useMemo } from 'react';
import { useSettings } from '@/data/Settings';
import { usePathname } from 'next/navigation';
import { loadExternalScripts, removeScripts } from '@/utils/loadExternalScripts';
import { getPageLoadScripts } from '@/utils/getPageLoadScripts';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { PAGE_TOKEN_NAME_CATEGORY, PAGE_TOKEN_NAME_PRODUCT } from '@/data/constants/layout';
import { useHelloRetailSearch } from '@/data/Content/HelloRetailSearch';
import { ContentProvider } from '@/data/context/content';
import { useProduct } from '@/data/Content/Product';
import { isConsentCheckerAvailable } from '@/utils/isConsentCheckerAvailable';
import { getHelloRetailInitScripts } from '@/utils/getHelloRetailInitScripts';
import { ItnlIntegrationScript } from '@/data/types/ItnlIntegrationScript';
import { getCookieValueFromConsent } from '@/utils/getCookieValueFromConsent';

type PageProps = {
	meta: Meta;
	layout: Layout;
	theme: Theme;
	additives?: { [key: string]: SxProps | ((theme: Theme) => SxProps) };
};

export const PageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const currentPath = usePathname();
	const { data:pageData } = usePageDataFromId();	
	const  {categoryCanonicalPath, pageNumber, prevPageLink, nextPageLink} = useCatalogEntryList(pageData?.tokenName===PAGE_TOKEN_NAME_CATEGORY?pageData?.tokenValue:'');
	const { settings } = useSettings();
	const {
		ifKlarnaexists,
		siteLinkDomainName,
		homePageTokenValue,
		isHelloRetailSearchEnabled,
		helloRetailUUID
	} = useMemo(()=>{
		return {
			ifKlarnaexists:settings?.userData.GroupOtherPaymentTypes?.includes('KLARNA'),
			siteLinkDomainName: settings.userData.SiteLink_DomainName,
			homePageTokenValue:settings.userData.HomePageTokenValue,
			isHelloRetailSearchEnabled : settings?.userData?.HelloRetailSearchEnabled === "true",
			helloRetailUUID : settings.userData.HelloRetailUUID
		}
	},[settings]);

	const pageType = useMemo(()=>homePageTokenValue===pageData?.tokenExternalValue?'HomePage':pageData?.page.type,[pageData]);

	
	const { product } =  useProduct({ id: pageData?.tokenName===PAGE_TOKEN_NAME_PRODUCT?pageData?.tokenExternalValue:undefined });
	const productImage = product?.fullImage;


	const scripts = useMemo(()=>getPageLoadScripts({ifKlarnaexists,settings, pageType}),[ifKlarnaexists,settings,pageType]);

	const {meta_title, meta_description} = useMemo(()=>{
		return pageData?.tokenName===PAGE_TOKEN_NAME_CATEGORY && pageNumber > 1 ? {
			meta_title: `Pagina ${pageNumber} | ${meta.title}`,
			meta_description: `Pagina ${pageNumber} | ${meta.description}`
		}:{
			meta_title: meta.title,
			meta_description:meta.description
		}
	},[pageNumber, meta.title]);

	useEffect(() => {
		if (currentPath){
			if (isHelloRetailSearchEnabled) {
				if (typeof window !== "undefined" && window.hrq?.push) {
					const marketingCookie = getCookieValueFromConsent("marketing") || window.Cookiebot?.consent?.marketing;
    				if(marketingCookie){
						scripts.push(getHelloRetailInitScripts(helloRetailUUID, !!window.__hasHelloretailInitialized));
						window.__hasHelloretailInitialized = true;
					}
					
				}
			}
			loadExternalScripts(scripts);
		}
		return () => {removeScripts(scripts)}
	}, [currentPath]);

	useEffect(() => {
		
		const cookiebotId = process.env.NEXT_PUBLIC_COOKIEBOT_DOMAIN_GROUP_ID;
		// Skip the logic if cookiebotId not empty
        // If cookiebotId is present, Cookiebot is loaded directly via code instead of GTM.
		// This works in Safari Incognito mode as well, so the fallback logic is not required.
		if (cookiebotId) return; 

		let helloRetailInitScripts: ItnlIntegrationScript[] = [];

		const helloRetailFallbackInitWhenConsentFails = async () => {
			const isConsentCheckAvailable = await isConsentCheckerAvailable();
			if (isConsentCheckAvailable) return;
			if (settings.userData.HelloRetailSearchEnabled === "true") {
				if (typeof window !== "undefined" && window.hrq?.push) {
					helloRetailInitScripts.push(getHelloRetailInitScripts(helloRetailUUID, !!window.__hasHelloretailInitialized));
					loadExternalScripts(helloRetailInitScripts);
					window.__hasHelloretailInitialized = true;
				}
			}
		};

		void helloRetailFallbackInitWhenConsentFails();

		return () => removeScripts(helloRetailInitScripts);
	}, [settings.userData, currentPath]);
	
	const Layout = layout && layoutManifest[layout.name] ? layoutManifest[layout.name] : () => null;
	const themeAdditives = useMemo(() => ({ additives }), [additives]);
	const helloRetailSearchValues = useHelloRetailSearch();
	const isHelloRetailValuesNeeded = useMemo(()=>isHelloRetailSearchEnabled 
			&& (pageData?.tokenExternalValue === "SearchPage" || pageData?.page.type === "SearchPage"),
	[
		pageData?.tokenExternalValue,
		pageData?.page.type,
		isHelloRetailSearchEnabled
	]);
	return (
		<>
			<Head>
				<title>{meta_title}</title>
				<meta name="title" content={meta_title} /> 
				<meta name="description" content={meta_description} />
				<meta name="keywords" content={meta?.keywords} />
				<meta name="theme-color" content="navbar.primary"/>
				<link rel="icon" href={`${siteLinkDomainName}/favicon.ico`} />
				<link rel="apple-touch-icon" href={`${siteLinkDomainName}/apple-touch-icon.png`} />
				{meta.canonical? <link rel="canonical" href={`${siteLinkDomainName}${categoryCanonicalPath??meta.canonical}`} /> : null}
				{meta.robotsExcludeNoFollowPage ? <meta name="robots" content="noindex, nofollow" /> : null}
				{meta.robotsExcludeFollowPage ? <meta name="robots" content="noindex, follow" /> : null}
				<meta name="viewport" content="width=device-width,initial-scale=1"/>
				<meta name="format-detection" content="telephone=no"/>
				<meta property="og:title" content={meta_title} />
				<meta property="og:description" content={meta_description} />				
				{productImage?<meta property="og:type" content="product" /> : <meta property="og:type" content="website" /> }
				{meta.canonical?<meta property="og:url" content={`${siteLinkDomainName}${categoryCanonicalPath??meta.canonical}`} /> : null }				
				{productImage? <meta property="og:image" content={`https://image.intertoys.nl${productImage}`} />
				: <meta property="og:image" content="https://image.intertoys.nl/wcsstore/logo-intertoys.png" /> }  
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/Effra_W_Rg.woff`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/Chank_Chaloops_Bold.otf`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/Chank_Chaloops_Medium.ttf`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/MikadoBlack.otf`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/MikadoBold.otf`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/MikadoLight.otf`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/MikadoMedium.otf`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/MikadoRegular.otf`} as="font" crossOrigin="anonymous" />
				{prevPageLink ? <link rel="prev" href={prevPageLink} /> : null}
				{nextPageLink ? <link rel="next" href={nextPageLink} /> : null}
			</Head>
			<CustomerService />
			<CDNCacheOnloadMutation />
			<SelectedStoreSynchronizer />
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<ThemeSettingsProvider value={themeAdditives}>					
					{/* <SkipToMainContent /> */}
					{isHelloRetailValuesNeeded ? 
						<ContentProvider value={{...helloRetailSearchValues}}>
							<Layout layout={layout} />
						</ContentProvider>
					:	<Layout layout={layout} />}
					<Notifications />
					<SessionErrorDialog />
				</ThemeSettingsProvider>
			</ThemeProvider>
		</>
	);
};

export const dynamic = 'force-static';

export const StaticPageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const { settings } = useStaticSettings();

	return settings ? (
		<SettingProvider value={settings}>
			<PageBlock meta={meta} layout={layout} theme={theme} additives={additives} />
		</SettingProvider>
	) : null;
};
